// Sprint 20.3 PR-5 (§F + §G) — runner-side auto-clone toast.
//
// Renders a non-blocking toast in the bottom-right of the viewport
// when the backend is preparing a project clone on the picked runner
// before dispatching the first `agent.spawn_request`. Three states:
//
//   1. cloning — `<Spinner /> Preparing project on <runnerLabel>…`.
//      Visible only after a 5 s grace per spec §F (avoids flash on
//      `already_present` runs that resolve in < 200 ms).
//   2. failed  — mutates to an error pill with the founder copy
//      coming from the backend (`message` field, mapped to founder
//      English / Spanish in `founderCopyForCloneFailure()`). Renders
//      a Retry button (closes the toast — the user re-sends from
//      the composer, which re-triggers the spawn pipeline).
//   3. dismissed — entry deleted from `preparingByAgent` (either
//      `phase: 'ready'` from the backend OR Retry button click).
//
// The toast is per-currently-selected-agent, not a global stack:
//
//   - At most ONE clone is in flight per runner+project at any time
//     (EC-58 dedupe), so the per-agent shape is exact.
//   - The selection scoping mirrors how Cursor's "preparing model…"
//     pill scopes to the active editor session, not a global tray
//     (`founder-first-vision.mdc` §8.A — Cursor as default UX
//     reference).
//   - This avoids the "5 toasts piled up after refresh" failure mode
//     when the user backgrounds the app during a multi-agent burst.
//
// Mounting: `DashboardLayout` renders `<SpawnPreparingToast />` at
// the layout level so it stays visible across composer focus changes
// and keeps below z-index of dialogs/dropdowns.

import { useEffect, useRef, useState } from 'react';
import { useAppStore } from '../../stores/app.store';
import { Spinner } from '../atoms/Spinner';
import { ProjectCloneFailedDialog } from './ProjectCloneFailedDialog';

// Spec §F: "Si el clone tarda > 5s, el toast se vuelve visible
// (evita flash en clones rápidos)." We keep the entry in the store
// from t=0 so the dismiss-on-ready flow is observable in tests, but
// gate the visual reveal on this grace window.
const VISIBLE_AFTER_MS = 5_000;

export function SpawnPreparingToast() {
  const selectedAgentId = useAppStore((s) => s.selectedAgentId);
  const preparing = useAppStore((s) =>
    selectedAgentId ? s.preparingByAgent[selectedAgentId] : undefined,
  );
  const clearPreparing = useAppStore((s) => s.clearPreparing);
  const [graceElapsed, setGraceElapsed] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Reset the grace window whenever the agent or its preparing entry changes.
  useEffect(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setGraceElapsed(false);
    if (!preparing) return;
    if (preparing.phase === 'failed') {
      // Failures bypass the grace — operator should see them
      // immediately; nothing to "wait for" anymore.
      setGraceElapsed(true);
      return;
    }
    const elapsed = Date.now() - preparing.startedAt;
    const remaining = Math.max(0, VISIBLE_AFTER_MS - elapsed);
    timerRef.current = setTimeout(() => {
      setGraceElapsed(true);
    }, remaining);
    return () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [preparing, selectedAgentId]);

  if (!selectedAgentId || !preparing) return null;
  if (!graceElapsed) return null;

  if (preparing.phase === 'failed') {
    return (
      <ProjectCloneFailedDialog
        runnerId={preparing.runnerId}
        reason={preparing.reason}
        message={preparing.message}
        onDismiss={() => clearPreparing(selectedAgentId)}
      />
    );
  }

  // Sprint toast-topright-action-sync (spec D2): the pill lives in the same
  // top-right corner as the toast stack and must drop below it so neither
  // covers the other.
  //
  // DS.8 D7 — the OFFSET IS NO LONGER A CONSTANT, and that is the fix, not a
  // re-tune. The retired number encoded the old toast vendor's COLLAPSED
  // stacking (three toasts peeking 14 px each, so ~1 toast height + 2 peeks),
  // an arithmetic with no referent once `@ds/feedback/Toast`'s plain flex
  // column replaced it: that container's height grows LINEARLY with the toast
  // count AND with each toast's own wrapped height, so no fixed value is
  // right at more than one count.
  // [medido: the retired 6rem (96px) vs the DS container's real height — 0px
  // empty, 78px at one toast, 258px at three; getBoundingClientRect on
  // `.aos-toast-host > div` in the ZOrderScene story at 1440x900, n=1,
  // 2026-09-14. 6rem cleared ONE toast by 2px and fell 180px short of three.]
  // `ds/DsToaster.tsx` therefore publishes the live container height plus one
  // `--space-3` gap as `--aos-toast-stack-clearance` on `:root` (a
  // ResizeObserver, exact for any count and any wrap), and this reads it. The
  // fallback `0px` is what an unmounted toaster leaves, i.e. today's
  // un-offset position. env() + rem calc per design-discipline's calc
  // exception (Composer precedent).
  // `tests/visual/ds8-z-order.spec.ts` "spawn pill does not overlap" asserts
  // boundingBox non-intersection at one AND at three toasts — THAT is the
  // instrument; this comment only records the derivation.
  const topOffset =
    'calc(env(safe-area-inset-top) + 1rem + var(--aos-toast-stack-clearance, 0px))';

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="spawn-preparing-toast"
      style={{ top: topOffset }}
      className="fixed right-4 z-30 flex items-center gap-2 rounded-md border border-border bg-surface px-4 py-2 text-body text-foreground shadow-lg"
    >
      <Spinner size="sm" />
      <span>{`Preparing project on ${formatRunnerLabel(preparing.runnerId)}…`}</span>
    </div>
  );
}

// Founder-mode runner label. The backend ships the raw runner id
// (`vps1`, `runner-2`, …); Cursor-style we humanise to a short
// readable form. Falls back to the raw id when the format is
// unfamiliar so we never show an empty string.
function formatRunnerLabel(runnerId: string): string {
  const lower = runnerId.toLowerCase();
  const m = lower.match(/^(?:runner-|vps-?|host-?|node-?)(\d+)$/);
  if (m) return `Runner ${m[1]}`;
  if (lower.startsWith('runner-')) return `Runner ${lower.slice(7)}`;
  return runnerId;
}
